import {
  Button,
  CheckboxMenu,
  SegmentedButtons,
  Table,
  Tag,
  TextField,
  Typography,
} from '@scale/scaleui';
import { TeamLeadPermissionRole } from '@scale/shared/types/expertManagement';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { LoadingTable } from 'react-scale/components/ExpertManagement/ExpertManagementPage/LoadingTable';
import { SlidingDrawer } from 'react-scale/components/ExpertManagement/SlidingDrawer';
import { useDeleteTeamLeadPermissions } from 'react-scale/components/ExpertManagement/hooks/mutations/useDeleteTeamLeadPermissions';
import { useUpsertTeamLeadPermissions } from 'react-scale/components/ExpertManagement/hooks/mutations/useUpsertTeamLeadPermissions';
import { useFetchAdminGlobalTeamLeadPermissions } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchAdminTeamLeadPermissions';
import { useFetchPodTypes } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchPodTypes';
import { useTableFilteringContext } from 'react-scale/components/ExpertManagement/hooks/useTableFiltering';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import FilterComponent from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/SharedComponents/FilterComponent';

const userRoleOptions = Object.entries(TeamLeadPermissionRole).map(role => ({
  id: role[1],
  value: role[0],
}));

const userRoleOptionsMap = _.keyBy(userRoleOptions, 'id');

export const PermissionsManagement: React.FC = () => {
  const { data: podTypes } = useFetchPodTypes();
  const podTypeOptions = useMemo(
    () => podTypes?.podTypes.map((p: { _id: any; name: any }) => ({ id: p._id, value: p.name })),
    [podTypes?.podTypes],
  );
  const podTypeOptionsMap = useMemo(() => _.keyBy(podTypeOptions, 'id'), [podTypeOptions]);
  const podTypeMap = useMemo(() => _.keyBy(podTypes?.podTypes, '_id'), [podTypes?.podTypes]);
  const { data, isLoading, refetch } = useFetchAdminGlobalTeamLeadPermissions();
  const { mutateAsync: upsertPermissions, isLoading: upsertLoading } =
    useUpsertTeamLeadPermissions();
  const { mutateAsync: deletePermissions, isLoading: deleteLoading } =
    useDeleteTeamLeadPermissions();
  const [user, setUser] = useState<{ label: string; value: string } | undefined>(undefined);
  const [userRoles, setUserRoles] = useState<{ id: string; value: string }[]>([]);
  const [userPodTypes, setUserPodTypes] = useState<{ id: string; value: string }[]>([]);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [editMode, setEditMode] = useState<'edit' | 'create'>('edit');
  const [displayType, setDisplayType] = useState<'global' | 'pod_type'>('global');
  const [searchValue, setSearchValue] = useState('');
  const podTypeColumns = [
    {
      id: 'worker.name',
      header: 'User',
      cell: function UserCell(props: any) {
        const { name, email, id } = props.row.original.worker;
        if (!name) {
          return null;
        }
        return (
          <div>
            <div>{name}</div>
            <div className="text-xs">{email}</div>
            <div className="text-xs">{id}</div>
          </div>
        );
      },
    },
    {
      id: 'roleSort',
      header: 'Roles',
      cell: function RoleCell(current: any) {
        const { roles } = current.row.original;
        return (
          <div className="flex flex-col gap-2">
            {roles.map((tag: TeamLeadPermissionRole) => (
              <div key={tag}>
                <Tag variant="info">{tag}</Tag>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'podTypeSort',
      header: 'Permissioned Pods',
      cell: function PermissionedPodsCell(current: any) {
        const { permissionedPodTypes } = current.row.original;
        return (
          <div className="flex flex-col gap-2">
            {permissionedPodTypes.map((tag: string) => (
              <div key={podTypeMap[tag]?._id}>
                <Tag variant="info">{podTypeMap[tag]?.name ?? 'Unknown Pod Type'}</Tag>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'editButton',
      header: '',
      enableSorting: false,
      cell: function EditCell(current: any) {
        return (
          <Button
            onClick={() => {
              setEditMode('edit');
              setUser({
                value: current.row.original.worker.id,
                label: current.row.original.worker.email,
              });
              setUserRoles(
                (current.row.original.roles ?? []).map((r: string) => userRoleOptionsMap[r]) ?? [],
              );
              setUserPodTypes(
                (current.row.original.permissionedPodTypes ?? []).map(
                  (r: string) => podTypeOptionsMap[r],
                ) ?? [],
              );
              setDetailPanelOpen(true);
            }}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  const closeModal = useCallback(() => {
    setDetailPanelOpen(false);
    setUser(undefined);
    setUserRoles([]);
    setUserPodTypes([]);
  }, [setDetailPanelOpen, setUserRoles]);

  const onSave = useCallback(async () => {
    if (!user) {
      return;
    }
    await upsertPermissions({
      worker: user.value,
      roles: userRoles.map(r => r.id as TeamLeadPermissionRole),
      permissionedPodTypes: userPodTypes.map(r => r.id),
    });
    refetch();
    closeModal();
  }, [closeModal, refetch, upsertPermissions, user, userPodTypes, userRoles]);

  const onDelete = useCallback(async () => {
    if (!user) {
      return;
    }
    await deletePermissions(user.value);
    refetch();
    closeModal();
  }, [closeModal, deletePermissions, refetch, user]);

  const enrichedData = useMemo(
    () =>
      ((displayType === 'global' ? data?.globalPermissions : data?.podTypePermissions) ?? [])
        .filter(
          (p: any) =>
            p.worker.name.includes(searchValue) ||
            p.worker.email.includes(searchValue) ||
            p.worker.id.includes(searchValue),
        )
        .map((p: any) => ({
          ...p,
          permissionedPodTypeNames: p.permissionedPodTypes.map((per: any) =>
            podTypeOptionsMap[per].value.toLowerCase(),
          ),
          // sort by number of roles
          roleSort: p.roles.length,
          podTypeSort: p.permissionedPodTypes.length,
        }))
        .sort((a, b) => (a.worker.name > b.worker.name ? 1 : -1)),
    [data, displayType, podTypeOptionsMap, searchValue],
  );

  const { filters, filteredData, fieldMap, setFilters, exactFieldOptionsMap, fieldOptions } =
    useTableFilteringContext({
      data: enrichedData,
      fieldFilters: {
        roles: { name: 'Role', type: 'in_only' },
        permissionedPodTypeNames: { name: 'Pod Types', type: 'in_only' },
      },
      exactFieldOptionsMap: {
        roles: Object.values(TeamLeadPermissionRole),
        permissionedPodTypeNames: podTypeOptions.map((p: any) => p.value),
      },
    });

  return (
    <>
      <div style={{ minWidth: '70%' }} className="flex flex-col gap-2">
        <div className="flex justify-between mt-2">
          <Typography.H4>Permissions</Typography.H4>
          <SegmentedButtons
            buttons={[
              { id: 'global', label: 'Global' },
              { id: 'pod_type', label: 'Pod Type' },
            ]}
            selected={displayType}
            onSelect={d => setDisplayType(d as any)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between">
            <TextField
              className="w-[300px]"
              placeholder="Search by name, email, or ID.."
              value={searchValue}
              onChange={t => setSearchValue(t.target.value)}
              trailingIcon={'xmark'}
              onTrailingIconClick={() => setSearchValue('')}
            />
            <div className="flex gap-2">
              <FilterComponent
                filters={filters}
                setFilters={setFilters}
                fieldMap={fieldMap}
                exactFieldOptionsMap={exactFieldOptionsMap}
                fieldOptions={fieldOptions}
              />
              <Button
                className="my-auto"
                onClick={() => {
                  setEditMode('create');
                  setDetailPanelOpen(true);
                }}
              >
                + Create
              </Button>
            </div>
          </div>
          {isLoading || !data ? (
            <LoadingTable columns={podTypeColumns} numRows={3} rowHeight={2} />
          ) : (
            <Table columns={podTypeColumns} data={filteredData} defaultPageSize={50} />
          )}
        </div>
      </div>
      <SlidingDrawer
        open={detailPanelOpen}
        onClose={closeModal}
        inputs={[
          {
            label: 'Name',
            component: (
              <SelectScaler
                placeholder="Select a user to assign permissions.."
                value={user}
                onChange={setUser}
                filterOption={() => true}
                disabled={editMode === 'edit'}
              />
            ),
          },
          {
            label: 'Roles',
            component: (
              <CheckboxMenu
                options={userRoleOptions}
                value={userRoles}
                onChange={(selected: { id: string; value: string }[]) => setUserRoles(selected)}
              />
            ),
          },
          {
            label: 'Pod Types',
            component: (
              <CheckboxMenu
                options={podTypeOptions}
                value={userPodTypes}
                onChange={(selected: { id: string; value: string }[]) => setUserPodTypes(selected)}
              />
            ),
          },
        ]}
        disableButtons={upsertLoading || deleteLoading}
        allowDelete={editMode === 'edit'}
        onSave={onSave}
        onDelete={onDelete}
        saveButtonText={editMode === 'edit' ? 'Save Changes' : 'Create'}
        width="32%"
      />
    </>
  );
};
